'use client';

import { useMemo, useState } from 'react';

import {
  ChartLegend,
  type ChartLegendItem,
} from '@/components/charts/_shared/ChartLegend';
import { RedListIndexChartIsland } from '@/components/charts/RedListIndexChartIsland';
import { GA_CHART_ID, GA_EVENTS, GA_PARAMS } from '@/constants/analytics';
import {
  RED_LIST_INDEX_CI_COLOR,
  RED_LIST_INDEX_COMPARISON_FILTER_ID,
  RED_LIST_INDEX_FLYWAYS,
  RED_LIST_INDEX_LINE_COLOR,
  STATE_FLYWAY_COLORS,
  STATE_FLYWAY_FILTERS,
  type RedListIndexFilterId,
  type RedListIndexFlywayId,
} from '@/constants/stateCharts';
import { redListIndexData } from '@/content/data/charts/red-list-index';
import { trackEvent } from '@/lib/analytics/trackEvent';
import { selectRedListIndexSeries } from '@/lib/charts/selectRedListIndexSeries';

import { RedListIndexFilters } from './RedListIndexFilters';
import styles from './RedListIndexFrame.module.css';
import { RedListIndexMobileInfo } from './RedListIndexMobileInfo';
import { RedListIndexSideStats } from './RedListIndexSideStats';

import type { Messages } from '@/content/messages';
import type { Locale } from '@/lib/i18n';

type RedListIndexChromeProps = {
  locale: Locale;
  charts: Messages['state']['charts'];
};

function getFlywayLabel(
  charts: Messages['state']['charts'],
  flywayId: RedListIndexFlywayId,
): string {
  const labelIndex = STATE_FLYWAY_FILTERS.indexOf(flywayId);
  return charts.filters[labelIndex] ?? flywayId;
}

/**
 * Client chrome for Red List Index: owns flyway filter; syncs chart + side stats.
 * Compact flyway carousel shares `chromeScope` with the chart column.
 */
export function RedListIndexChrome({ locale, charts }: RedListIndexChromeProps) {
  const [flywayFilter, setFlywayFilter] = useState<RedListIndexFilterId>('all');

  function handleFlywayFilterChange(next: RedListIndexFilterId) {
    setFlywayFilter(next);
    trackEvent(GA_EVENTS.chartFilter, {
      [GA_PARAMS.chartId]: GA_CHART_ID.redListIndex,
      [GA_PARAMS.flywayId]: next,
    });
  }
  const rli = charts.redListIndex;
  const isComparisonView = flywayFilter === RED_LIST_INDEX_COMPARISON_FILTER_ID;
  const flywayLabels = Object.fromEntries(
    RED_LIST_INDEX_FLYWAYS.map((flywayId) => [
      flywayId,
      getFlywayLabel(charts, flywayId),
    ]),
  ) as Record<RedListIndexFlywayId, string>;
  const legendItems: ChartLegendItem[] = isComparisonView
    ? RED_LIST_INDEX_FLYWAYS.map((flywayId) => ({
        label: flywayLabels[flywayId],
        color: STATE_FLYWAY_COLORS[flywayId],
        variant: 'line',
      }))
    : [
        {
          label: rli.legend.index,
          color: RED_LIST_INDEX_LINE_COLOR,
          variant: 'line',
        },
        {
          label: rli.legend.confidence,
          color: RED_LIST_INDEX_CI_COLOR,
          variant: 'swatch',
        },
      ];

  const { stats } = useMemo(
    () =>
      selectRedListIndexSeries(
        redListIndexData,
        flywayFilter === RED_LIST_INDEX_COMPARISON_FILTER_ID ? 'all' : flywayFilter,
      ),
    [flywayFilter],
  );

  return (
    <div className={styles.layout}>
      <div className={styles.chromeScope}>
        <div className={styles.filterColumn}>
          <RedListIndexFilters
            heading={rli.heading}
            charts={charts}
            flywayFilter={flywayFilter}
            onFlywayFilterChange={handleFlywayFilterChange}
          />
        </div>

        <div className={styles.chartColumn}>
          <div className={styles.chartPlate}>
            <RedListIndexChartIsland
              locale={locale}
              flywayFilter={flywayFilter}
              ariaLabel={rli.ariaLabel}
              showAllAriaLabel={rli.showAllAriaLabel}
              flywayLabels={flywayLabels}
              lowerRiskLabel={rli.lowerRiskLabel}
              higherRiskLabel={rli.higherRiskLabel}
              tableCaption={rli.tableCaption}
              flywayColumnLabel={rli.flywayColumnLabel}
              yearColumnLabel={rli.yearColumnLabel}
              indexColumnLabel={rli.indexColumnLabel}
              lowerColumnLabel={rli.lowerColumnLabel}
              upperColumnLabel={rli.upperColumnLabel}
            />
            <RedListIndexMobileInfo
              stats={stats}
              improvedCaption={rli.improvedCaption}
              deterioratedCaption={rli.deterioratedCaption}
              openLabel={rli.openInfo}
              closeLabel={rli.closeInfo}
              dialogLabel={rli.statsDialogLabel}
            />
          </div>
          <ChartLegend items={legendItems} />
        </div>
      </div>

      <div className={styles.statsColumn}>
        <RedListIndexSideStats
          stats={stats}
          improvedCaption={rli.improvedCaption}
          deterioratedCaption={rli.deterioratedCaption}
        />
      </div>
    </div>
  );
}
